<template>
  <fts-modal
    :append-to-body="appendToBody"
    :dynamic-screen="dynamicScreen"
    :width="width"
    :full-screen="fullScreen"
    :backdrop="backdrop"
    :escape-event="escapeEvent"
    :persistent="persistent"
    :header="header"
    :footer="isFooter"
    :header-class="headerClass"
    :content-class="contentClass"
    :footer-class="footerClass"
    :append-container-class="appendContainerClass"
    class="fts-client-modal"
    @opened="opened"
    @close="beforeClose"
    @closed="$emit('closed')"
    @focus="$emit('focus')"
    @blur="$emit('blur')"
    v-if="showDialog"
  >
    <!-- header -->
    <template #header>
      <!-- header wrapper -->
      <div class="client-modal-header">
        <div class="client-modal-header__icon" v-if="isIcon">
          <slot name="icon" />
        </div>
        <div class="client-modal-header__wrapper">
          <!-- title -->
          <slot name="title">
            <div class="client-modal-header-title">{{title}}</div>
          </slot>
          <!-- description -->
          <slot class="description" v-if="isDescription">
            <div class="client-modal-header-description">{{description}}</div>
          </slot>
        </div>
      </div>
      <!-- actions -->
      <slot name="header-actions">
        <div class="client-modal-header-actions">
          <slot name="actions" />
          <fts-btn
            text
            icon
            :disabled="disabledClose" 
            @click="beforeClose"
          >
            <font-awesome-icon :icon="['fas', 'times']" />
          </fts-btn>
        </div>
      </slot>
    </template>
    <!-- body -->
    <template #body>
      <slot name="body" />
    </template>
    <!-- footer -->
    <template #footer>
      <slot name="footer">
        <!-- footer left -->
        <div class="client-modal-footer__l">
          <slot name="footerLeft" />
        </div>
        <!-- footer right -->
        <div class="client-modal-footer__r">
          <slot name="footerRight" />
        </div>
      </slot>
    </template>
  </fts-modal>
</template>

<script>
import FTSModal from "./FTSModal";
import FTSBtn from "./FTSBtn";

export default {
  components: {
    "fts-modal": FTSModal,
    "fts-btn": FTSBtn,
  },
  props: {
    appendToBody: Boolean,
    fullScreen: Boolean,
    dynamicScreen: Boolean,
    width: String,
    backdrop: {
      type: Boolean,
      default: true
    },
    escapeEvent: {
      type: Boolean,
      default: true
    },
    persistent: Boolean,
    header: {
      type: Boolean,
      default: true
    },
    footer: {
      type: Boolean,
      default: true
    },
    appendHeaderClass: String,
    appendContentClass: String,
    appendFooterClass: String,
    appendContainerClass: String,
    title: String,
    description: String,
    disabledClose: Boolean,
  },
  data: () => ({
    showDialog: false,
    beforeCloseHandler: null,
  }),
  methods: {
    show(item) {
      if (item && item.beforeCloseHandler)
        this.beforeCloseHandler = item.beforeCloseHandler;

      this.showDialog = true;
    },
    opened() {
      this.$emit("opened");
    },
    beforeClose() {
      if (this.disabledClose) return;

      if (this.beforeCloseHandler && typeof this.beforeCloseHandler === 'function') {
        this.beforeCloseHandler();  
      } else {
        this.close();
      }
    },
    close() {
      this.showDialog = false;
      this.$emit("close");
    }
  },
  computed: {
    isDescription() {
      return (
        this.description || 
        Boolean(this.$slots.description) || 
        Boolean(this.$scopedSlots.description)
      );
    },
    isIcon() {
      return Boolean(this.$slots.icon) || Boolean(this.$scopedSlots.icon);
    },
    isFooterLeft() {
      return Boolean(this.$slots.footerLeft) || Boolean(this.$scopedSlots.footerLeft);
    },
    isFooterRight() {
      return Boolean(this.$slots.footerRight) || Boolean(this.$scopedSlots.footerRight);
    },
    isFooter() {  
      return (
        this.footer &&
        (
          Boolean(this.$slots.footer) || 
          Boolean(this.$scopedSlots.footer) || 
          this.isFooterLeft ||
          this.isFooterRight
        )
      );
    },
    headerClass() {
      let classes = "client-modal__header";
      if (this.appendHeaderClass)
        classes += ` ${this.appendHeaderClass}`;

      return classes;
    } , 
    contentClass() {
      let classes = "client-modal__body";
      if (this.appendContentClass)
        classes += ` ${this.appendContentClass}`;

      return classes;
    },
    footerClass() {
      let classes = "client-modal__footer";
      if (this.isFooterLeft || this.isFooterRight)
        classes += " row";
      if (this.appendFooterClass)
        classes += ` ${this.appendFooterClass}`;
      
      return classes;
    }
  }
}
</script>

<style lang="scss">
@import "./src/assets/scss/vars.scss";

.fts-client-modal {
  .client-modal__header {
    display: flex;
    align-items: start;
    padding: 1rem;
    border-bottom: 1px solid var(--fts-border);
    .client-modal-header {
      min-width: 0;
      flex: 1;
      display: flex;
      align-items: start;
      .client-modal-header__icon {
        height: 35px;
        display: flex;
        align-items: center;
        margin-right: 0.5rem;
        font-size: 1.125rem;
        line-height: 1.5;
        color: var(--fts-primary);
        > * {
          line-height: inherit;
        }
      }
      .client-modal-header__wrapper {
        min-width: 0;
        flex: 1;
        .client-modal-header-title {
          @include text-overflow-ellipsis;
          height: 35px;
          display: flex;
          align-items: center;
          font-weight: 500;
          font-size: 1.125rem;
          line-height: 1.5;
          color: var(--fts-text);
        }
        .client-modal-header-description {
          font-size: 0.75rem;
          line-height: 1.5;
          color: var(--fts-default);
        }
      }
    }
    .client-modal-header-actions {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      padding-left: 0.75rem;
    }
  }
  .client-modal__body {
    padding: 1rem;
  }
  .client-modal__footer {
    padding: 1rem;
    border-top: 1px solid var(--fts-border);
    text-align: right;
    &.row {
      display: flex;
    }
    .client-modal-footer__l {
      @include flex-gap($gap: 0.5rem, $width: auto);
    }
    .client-modal-footer__r {
      @include flex-gap($gap: 0.5rem, $width: auto);
      justify-content: flex-end;
      flex: 1;
    }
  }
  /* spinner */
  .client-modal-spinner {
    padding: 2rem 0;
  }
}
</style>